import { add } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import moment from 'moment-timezone';
import { IFilterItem } from '../features/detail/interfaces/IFilterItem';
import { ITask } from '../features/tasks/interfaces/ITask';
import { Evidence } from '../features/tasks/types/evidence';
import {
  TaskCategory,
  TaskCategoryMapping,
} from '../features/tasks/types/taskTypes';
import { PA, RateClass } from './constants/decision';
import { Products } from './constants/products';
import { RiderNames } from './constants/rider';
import { TaskState } from './constants/states';
/**
 * Helper to get time zone offsetted date
 * @param {string} timestamp must be formatted as ISO YYYY-MM-DDTHH:mm:ss.SSSZ
 *
 * NOTE: This helper is only for converting a date in ISO UTC format: YYYY-MM-DDTHH:mm:ss.SSSZ.
 * To convert a lower-resolution date string, please use a different helper
 *
 * @return {string} formatted timestamp in local time zone
 */
export const getFormattedTimestamp = (
  timestamp: string,
  format = 'MM/DD/YYYY · hh:mm A z',
): string => {
  if (timestamp && moment(timestamp).isValid()) {
    return (
      moment(timestamp)
        // @ts-ignore
        .tz(moment.tz.guess())
        .format(format)
    );
  } else {
    throw new Error(
      `Timestamp is not formmated as YYYY-MM-DDTHH:mm:ss.SSSZ - ${timestamp}`,
    );
  }
};

/**
 * Native implementation to check whether an object is empty
 * Taken from https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore#_isempty
 *
 * @param obj
 */
export const isEmpty = (obj: any): boolean => {
  return (
    [Object, Array].includes((obj || {}).constructor) &&
    !Object.entries(obj || {}).length
  );
};

/**
 * Function to check if there is any evidence
 * @param {Evidence} evidence
 * @returns {boolean}
 */
export const evidenceExists = (evidence?: Evidence): boolean => {
  if (evidence) {
    return evidence.documents && evidence.documents.length > 0;
  }
  return false;
};

/**
 * Function to check if the application tasks are all
 * either a default task or completed/waived
 * @param {ITask[]} tasks
 * @return {boolean}
 */
export const tasksResolved = (tasks: ITask[]): boolean => {
  if (!tasks || tasks.length === 0) {
    return true;
  }
  const resolvedStates = [TaskState.COMPLETED, TaskState.WAIVED];

  // Synthetic/section-anchor categories (Decision, Overview, Notes, etc.) have
  // no resolvable state and should be treated as auto-resolved.
  const autoResolvedCategories: ReadonlySet<TaskCategory> = new Set([
    TaskCategory.DEFAULT_TASK,
    TaskCategory.NOTES_TASK,
  ]);
  return (
    tasks.filter(
      task =>
        autoResolvedCategories.has(TaskCategoryMapping[task.taskType]) ||
        resolvedStates.includes(task.state),
    ).length === tasks.length
  );
};

/**
 * Function to check if the current decision is OTAF
 * This is the same logic as what exists in Nora's backend
 * @param {IFilterItem} appliedRateClass
 * @param {IFilterItem} selectedRateClass
 * @param {IFilterItem} appliedTobaccoClass
 * @param {IFilterItem} selectedTobaccoClass
 * @param {IFilterItem} appliedPa
 * @param {IFilterItem} selectedPaOption
 * @param {string} product - optional product name
 * @param {string} stateAtSubmission - optional state
 * @return {boolean}
 */
export const isOtaf = (
  selectedRateClass: IFilterItem | null,
  appliedTobaccoClass: IFilterItem | null,
  selectedTobaccoClass: IFilterItem | null,
  selectedPaOption: IFilterItem,
  appliedRiders: string[],
  eligibleRiders: string[],
  product?: string,
  stateAtSubmission?: string,
): boolean => {
  // OTAF needed - tobacco downgraded
  if (
    appliedTobaccoClass &&
    selectedTobaccoClass &&
    appliedTobaccoClass.value > selectedTobaccoClass.value
  ) {
    return true;
  }

  // Only check if rate class is STANDARD
  const isSubstandard =
    selectedRateClass &&
    selectedRateClass.label === RateClass.STANDARD &&
    selectedPaOption.label !== PA.PA100.toString();

  if (isSubstandard) {
    return true;
  }

  // Check if riders changed
  const ridersChanged =
    appliedRiders.length !== eligibleRiders.length ||
    !appliedRiders.every(rider => eligibleRiders.includes(rider));

  if (ridersChanged) {
    // Suppress OTAF for Choice non-CA when:
    // - LIVING_BENEFITS is eligible
    // - TERMINAL_ILLNESS is ineligible
    // - Decision is not substandard
    const isChoiceNonCA =
      product === Products.CHOICE &&
      stateAtSubmission &&
      stateAtSubmission !== 'CA';
    const isLivingBenefitsEligible = eligibleRiders.includes(
      RiderNames.LIVING_BENEFITS,
    );
    const ineligibleRiders = appliedRiders.filter(
      r => !eligibleRiders.includes(r),
    );
    const isTerminalIllnessIneligible = ineligibleRiders.includes(
      RiderNames.TERMINAL_ILLNESS,
    );

    if (
      isChoiceNonCA &&
      !isSubstandard &&
      isLivingBenefitsEligible &&
      isTerminalIllnessIneligible
    ) {
      // OTAF suppressed for Choice non-CA with living benefits eligible and terminal illness ineligible
      return false;
    }

    return true;
  }

  return false;
};

/**
 * Format number by adding an ordinal suffix eg 1 -> 1st
 * @param i {number} number
 */
export const ordinalSuffix = (i: number): string => {
  const j = i % 10;
  const k = i % 100;
  if (j === 1 && k !== 11) {
    return `${i}st`;
  }

  if (j === 2 && k !== 12) {
    return `${i}nd`;
  }

  if (j === 3 && k !== 13) {
    return `${i}rd`;
  }

  return `${i}th`;
};

/**
 * Calculate SLA timer that will be displayed in SLA column in dashboard. Each
 * task must be completed in 24h from the time it was created. Timer should
 * round up, so 15 minutes left should be 1 hour, 23:15 left should be 24h. In
 * increments of hours (no minutes). Once the timer hits 0 and the task is still
 * not completed, the timer should go into the negatives.
 *
 * @param date            {Date} Date when a task was created.
 * @param [referenceTime] {Date} Reference point in time.
 */

const startHour = 8;
const stopHour = 18;
const HOUR_IN_MS = 60 * 60 * 1000;
const timezone = 'America/Chicago';

function isWorkingHour(date: Date): boolean {
  return (
    isWorkDay(date) &&
    date.getHours() >= startHour &&
    date.getHours() < stopHour
  );
}
function isWorkDay(date: Date): boolean {
  // for now only check if it is weekday
  return ![0, 6].includes(date.getDay());
}

// returns no of hours remaining until deadline
export const calculateSla = (
  date: Date,
  referenceTime = new Date(),
): number => {
  let start = utcToZonedTime(date, timezone);
  let deadline = start;
  if (isWorkingHour(start)) {
    // inside working hour, will stop at the next work day the same hour
    while (true) {
      deadline = add(deadline, { days: 1 });
      if (isWorkDay(deadline)) {
        break;
      }
    }
  } else {
    // if start hour passed, we go to next day
    if (start.getHours() >= startHour) {
      start = add(start, { days: 1 });
    }
    // set to start hour
    start.setHours(startHour, 0, 0, 0);
    deadline = start;
    while (true) {
      // find the next working day, sla will finish next day at start hour
      if (isWorkDay(deadline)) {
        deadline = add(deadline, { days: 1 });
        break;
      }
      deadline = add(deadline, { days: 1 });
    }
  }
  deadline = zonedTimeToUtc(deadline, timezone);
  const hoursTillDeadline = Math.ceil(
    (deadline.getTime() - referenceTime.getTime()) / HOUR_IN_MS,
  );

  return hoursTillDeadline;
};
